import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useCommonClient } from '@/app/common/commonClientProvider';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { URLParameters } from '@/app/common/urlParameters';
import { trackingProductSelected } from '@/app/common/tracking/trackingProductSelected.ts';
import { getTrackingVcSearchId } from '@/app/common/tracking/trackingUtils.ts';
import { createFlightClickedPayload } from '@/app/common/starTrack/events/flightClicked.ts';
import { createFlightDetailsClickedPayload } from '@/app/common/starTrack/events/flightDetailsClicked.ts';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { useSelectedMealPlan } from '@/app/components/hotel-detail/SelectedMealPlanProvider';
import useSelectedTransport from '@/app/components/hotel-detail/hooks/useSelectedTransport';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { getActionId } from '@/app/components/hotel-detail/lib/tracking';
import getSearchParams from '@/app/components/search/common/getSearchParams';

/**
 * MPH-646: fires the DP-evolution book funnel step — FLIGHT_CLICKED + FLIGHT_DETAILS_CLICKED
 * (when a transport is part of the package) and DETAIL_BOOK_CLICKED — on the click that goes to the
 * review step: the flight page's desktop trip-overview Book button and mobile Continue button on DP,
 * and the rooms step's Continue on Hotel Only, which has no flight page to book from (MPH-935).
 *
 * In Evo there are no intermediate flight steps, so the three events must represent the same
 * funnel point: viewing or confirming flights in the change-flight popup no longer emits them
 * (see ChangeTransportDetailsModalProvider / useChangeTransportHandler). The flightClickId is
 * the current flight-viewing session's id (see rotateEvoFlightClickId in pageWrapperClientDetails),
 * shared by the three payloads and by CHOOSE_FLIGHT_SHOWN, BRANDED_FARES_* and CLICK2CHECKOUT so
 * the whole session joins on the same id.
 */
export function useTrackEvoBookClicked() {
  const { starTrack, handleFlightClicked } = useCommonClient();
  const { searchInfoData, commonData, localization } = usePageWrapperData();
  const { selectedMealPlan, selectedRoom } = useSelectedMealPlan();
  const { data: selectedTransportData } = useSelectedTransport();
  const { offersData } = useOffersContext();
  const { accommodationOnly } = useUrlSearchParams();
  const initialParams = useInitialParams();

  // MPH-935: Hotel Only books straight from the rooms step, where the caller selects and continues in the
  // same handler — the provider state it just set is not readable here yet, so those call sites pass the
  // room/meal plan explicitly. The flight-page callers keep using the committed page-level selection.
  return useCallback(
    (room?: RoomType, mealPlan?: MealPlanType) => {
      const targetMealPlan = mealPlan ?? selectedMealPlan;
      const targetRoom = room ?? selectedRoom;
      if (!targetMealPlan) return;

      const searchParams = getSearchParams();
      const searchId = searchParams[URLParameters.SEARCH_ID];
      const transport = accommodationOnly ? undefined : selectedTransportData?.transports[0];
      // The current flight-viewing session's id; the fallback covers states created before the
      // seed/rotation existed (it is then stored so CLICK2CHECKOUT still joins).
      const flightClickId = initialParams.flightClickId || uuidv4().slice(0, 32);

      if (transport) {
        if (!initialParams.flightClickId) {
          handleFlightClicked(flightClickId, transport.eligibleBrandedFares);
        }
        starTrack.flightClicked(
          createFlightClickedPayload({
            searchId: searchId || '',
            productDetailKey: initialParams.productDetailKey,
            wasDefaultFlight: !initialParams.defaultTransportId || transport.id === initialParams.defaultTransportId,
            transport,
            flightClickId,
            vcSearchId: getTrackingVcSearchId(offersData, searchParams[URLParameters.VC_SEARCH_ID]),
          })
        );
        starTrack.flightDetailsClicked(
          createFlightDetailsClickedPayload({
            searchId: searchId || '',
            productDetailKey: initialParams.productDetailKey,
            flightClickId,
          })
        );
      }

      trackingProductSelected({
        starTrack,
        searchParams,
        searchInfoData,
        commonData,
        localization,
        initialParams,
        offersData,
        actionId: getActionId(initialParams.actionIdSeed, searchId),
        selectedMealPlan: targetMealPlan,
        roomId: targetRoom?.roomId || '',
        ...(transport ? { flightClickId } : undefined),
      });
    },
    [
      starTrack,
      handleFlightClicked,
      searchInfoData,
      commonData,
      localization,
      selectedMealPlan,
      selectedRoom,
      selectedTransportData,
      offersData,
      accommodationOnly,
      initialParams,
    ]
  );
}
